import React, { useState, useEffect } from 'react';
import { apiCall } from '../lib/api.js';
import { CATEGORIES } from '../lib/scorecard-config.js';

const ATTR_MAP = {};
CATEGORIES.forEach(cat => cat.attributes.forEach(a => { ATTR_MAP[a.id] = { label: a.label, weight: a.weight, category: cat.name }; }));

const CAT_STYLES = {
  'Verification & Account Handling': { border: 'border-l-emerald-500', icon: 'ti-shield-check', iconColor: 'text-emerald-600' },
  'Accuracy & Knowledge': { border: 'border-l-purple-500', icon: 'ti-book', iconColor: 'text-purple-600' },
  'Resolution & Effort': { border: 'border-l-blue-500', icon: 'ti-target', iconColor: 'text-blue-600' },
  'Empathy & Tone': { border: 'border-l-pink-500', icon: 'ti-heart', iconColor: 'text-pink-600' },
  'Communication & Clarity': { border: 'border-l-amber-500', icon: 'ti-message-dots', iconColor: 'text-amber-600' },
  'Responsiveness & Documentation': { border: 'border-l-cyan-500', icon: 'ti-clock', iconColor: 'text-cyan-600' },
};

const PILL = {
  'Meets': 'bg-emerald-50 text-emerald-700 border-emerald-300',
  'Partial': 'bg-amber-50 text-amber-700 border-amber-300',
  'Misses': 'bg-red-50 text-red-700 border-red-300',
  'N/A': 'bg-gray-100 text-gray-500 border-gray-200',
};

const BAND_STYLES = {
  'Excellent': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Good': 'bg-blue-50 text-blue-700 border-blue-200',
  'Okay': 'bg-amber-50 text-amber-700 border-amber-200',
  'Needs improvement': 'bg-orange-50 text-orange-700 border-orange-200',
  'Failed': 'bg-red-50 text-red-700 border-red-200',
};

export default function EvaluationDetail({ evaluation, onBack, onEdit }) {
  const ev = evaluation;
  const scores = ev.scores || {};
  const deductions = ev.deductions || [];
  const autofails = ev.autofails || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600 transition">
          <i className="ti ti-arrow-left text-lg" aria-hidden="true"></i>
        </button>
        <h2 className="text-lg font-medium flex-1">Scorecard</h2>
        {onEdit && (
          <button onClick={() => onEdit(evaluation)}
            className="px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 text-xs font-medium flex items-center gap-1.5 transition">
            <i className="ti ti-pencil text-sm" aria-hidden="true"></i> Edit evaluation
          </button>
        )}
      </div>

      {/* Score hero */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="text-center">
            <div className={'text-5xl font-medium ' + (ev.autofail_triggered ? 'text-red-500' : 'text-gray-900')}>
              {Math.round((ev.final_score || 0) * 100)}<span className="text-xl text-gray-400">%</span>
            </div>
            <span className={'mt-2 inline-block text-xs font-medium px-3 py-1 rounded-full border ' + (BAND_STYLES[ev.rating_band] || '')}>{ev.rating_band}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">#{ev.ticket_number} <span className="font-normal text-gray-500">— {ev.ticket_subject}</span></div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
              <span><i className="ti ti-user text-xs mr-1" aria-hidden="true"></i>{ev.agent_name}</span>
              <span><i className="ti ti-user-check text-xs mr-1" aria-hidden="true"></i>{ev.evaluator_email}</span>
              {ev.company && ev.company !== 'sabrent' && (
                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium capitalize">{ev.company}</span>
              )}
              <span><i className="ti ti-calendar text-xs mr-1" aria-hidden="true"></i>{ev.eval_date}</span>
              {ev.customer_name && <span><i className="ti ti-users text-xs mr-1" aria-hidden="true"></i>{ev.customer_name}</span>}
              {ev.message_type && <span><i className="ti ti-mail text-xs mr-1" aria-hidden="true"></i>{ev.message_type}</span>}
            </div>
            {ev.appeal_adjusted && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-purple-600">
                <i className="ti ti-refresh text-sm" aria-hidden="true"></i>
                Score adjusted via appeal
              </div>
            )}
            {ev.edited && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ti ti-pencil text-sm" aria-hidden="true"></i>
                Edited {ev.edited_at ? new Date(ev.edited_at).toLocaleDateString() : ''}
              </div>
            )}
          </div>
          <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{ev.agent_name}</span>
        </div>
      </div>

      {/* Interaction summary */}
      {ev.interaction_summary && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-2">
            <i className="ti ti-file-text text-gray-400" aria-hidden="true"></i> Interaction summary
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">{ev.interaction_summary}</p>
        </div>
      )}

      {/* Score breakdown by category */}
      <div className="space-y-3 mb-4">
        {CATEGORIES.map(cat => {
          const s = CAT_STYLES[cat.name] || {};
          const catAttrs = cat.attributes.map(a => ({ ...a, rating: scores[a.id] || 'N/A' }));
          const catWeight = cat.attributes.reduce((sum, a) => sum + a.weight, 0);
          const catEarned = catAttrs.reduce((sum, a) => {
            if (a.rating === 'N/A') return sum;
            if (a.rating === 'Meets') return sum + a.weight;
            if (a.rating === 'Partial') return sum + a.weight * 0.5;
            return sum;
          }, 0);
          const catApplicable = catAttrs.reduce((sum, a) => a.rating === 'N/A' ? sum : sum + a.weight, 0);
          const catPct = catApplicable > 0 ? Math.round((catEarned / catApplicable) * 100) : null;

          return (
            <div key={cat.name} className={'bg-white rounded-xl border border-gray-200 overflow-hidden border-l-[3px] ' + (s.border || '')}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <i className={'ti ' + (s.icon || '') + ' ' + (s.iconColor || '')} aria-hidden="true"></i>
                  {cat.name}
                </h3>
                <div className="flex items-center gap-2">
                  {catPct != null && <span className="text-xs text-gray-500">{catPct}%</span>}
                </div>
              </div>
              {catAttrs.map(attr => (
                <div key={attr.id} className="px-4 py-2.5 border-b border-gray-50 last:border-0 flex items-center gap-2">
                  <span className="flex-1 text-sm text-gray-700">{attr.label} <span className="text-[11px] text-gray-400">({attr.weight})</span></span>
                  <span className={'px-2.5 py-1 rounded-full text-[11px] font-medium border ' + (PILL[attr.rating] || PILL['N/A'])}>{attr.rating}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Deductions */}
      {deductions.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-2">
            <i className="ti ti-alert-triangle text-amber-500" aria-hidden="true"></i> Process deductions
          </h3>
          <div className="text-sm text-gray-600">{deductions.length} deduction(s) applied (-{deductions.length * 5}%)</div>
        </div>
      )}

      {/* Autofails */}
      {autofails.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 border-l-[3px] border-l-red-400 p-4 mb-3">
          <h3 className="text-sm font-medium text-red-700 flex items-center gap-2 mb-2">
            <i className="ti ti-alert-octagon" aria-hidden="true"></i> Behavioral autofail triggered
          </h3>
          <div className="text-sm text-red-600">{autofails.length} autofail(s) — score set to 0%</div>
        </div>
      )}

      {/* Notes */}
      {ev.notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-2">
            <i className="ti ti-notes text-gray-400" aria-hidden="true"></i> Evaluator notes
          </h3>
          <p className="text-sm text-gray-600">{ev.notes}</p>
        </div>
      )}
    </div>
  );
}
